/**
 * Query Result Caching Infrastructure
 * Implements in-memory caching for expensive aggregation queries
 */

interface CacheEntry<T> {
	data: T;
	timestamp: number;
	ttl: number; // Time to live in milliseconds
}

export interface CacheStats {
	hits: number;
	misses: number;
	sets: number;
	evictions: number;
}

class QueryCache {
	private cache = new Map<string, CacheEntry<unknown>>();
	private stats: CacheStats = {
		hits: 0,
		misses: 0,
		sets: 0,
		evictions: 0,
	};

	private readonly maxSize = 1000; // Maximum cache entries
	private readonly cleanupInterval = 60000; // 1 minute cleanup interval

	constructor() {
		// Start cleanup interval
		setInterval(() => this.cleanup(), this.cleanupInterval);
	}

	/**
	 * Get cached result if available and not expired
	 */
	get<T>(key: string): T | null {
		const entry = this.cache.get(key);

		if (!entry) {
			this.stats.misses++;
			return null;
		}

		// Check if expired
		if (Date.now() - entry.timestamp > entry.ttl) {
			this.cache.delete(key);
			this.stats.misses++;
			return null;
		}

		this.stats.hits++;
		return entry.data as T;
	}

	/**
	 * Set cache entry with TTL
	 */
	set<T>(key: string, data: T, ttlMs: number): void {
		// Evict oldest entries if cache is full
		if (this.cache.size >= this.maxSize) {
			this.evictOldest();
		}

		this.cache.set(key, {
			data,
			timestamp: Date.now(),
			ttl: ttlMs,
		});

		this.stats.sets++;
	}

	/**
	 * Invalidate cache entries by key pattern
	 */
	invalidatePattern(pattern: string): number {
		let removed = 0;
		for (const key of this.cache.keys()) {
			if (key.includes(pattern)) {
				this.cache.delete(key);
				removed++;
			}
		}
		return removed;
	}

	/**
	 * Clear specific cache entry
	 */
	invalidate(key: string): boolean {
		return this.cache.delete(key);
	}

	/**
	 * Clear all cache entries
	 */
	clear(): void {
		this.cache.clear();
		this.stats = {
			hits: 0,
			misses: 0,
			sets: 0,
			evictions: 0,
		};
	}

	/**
	 * Get cache statistics
	 */
	getStats(): CacheStats & { size: number; hitRate: number } {
		const total = this.stats.hits + this.stats.misses;
		return {
			...this.stats,
			size: this.cache.size,
			hitRate: total > 0 ? (this.stats.hits / total) * 100 : 0,
		};
	}

	/**
	 * Cleanup expired entries
	 */
	private cleanup(): void {
		const now = Date.now();
		let removed = 0;

		for (const [key, entry] of this.cache.entries()) {
			if (now - entry.timestamp > entry.ttl) {
				this.cache.delete(key);
				removed++;
			}
		}

		this.stats.evictions += removed;
	}

	/**
	 * Evict oldest entries when cache is full
	 */
	private evictOldest(): void {
		let oldestKey = "";
		let oldestTime = Date.now();

		for (const [key, entry] of this.cache.entries()) {
			if (entry.timestamp < oldestTime) {
				oldestTime = entry.timestamp;
				oldestKey = key;
			}
		}

		if (oldestKey) {
			this.cache.delete(oldestKey);
			this.stats.evictions++;
		}
	}
}

// Global cache instance
export const queryCache = new QueryCache();

// Cache TTL constants (in milliseconds)
export const CACHE_TTL = {
	USER_STATS: 2 * 60 * 60 * 1000, // 2 hours
	FACEPILE_DATA: 30 * 60 * 1000, // 30 minutes
	SOCIAL_COUNTS: 5 * 60 * 1000, // 5 minutes
	ARTIST_STATS: 60 * 60 * 1000, // 1 hour
	FESTIVAL_STATS: 60 * 60 * 1000, // 1 hour
	SEARCH_RESULTS: 10 * 60 * 1000, // 10 minutes
} as const;

/**
 * Helper function to create cache keys
 */
export function createCacheKey(
	type: string,
	...params: (string | number)[]
): string {
	return `${type}:${params.join(":")}`;
}

/**
 * Wrapper function for cached query execution
 */
export async function withCache<T>(
	key: string,
	ttlMs: number,
	queryFn: () => Promise<T>,
): Promise<T> {
	// Try to get from cache first
	const cached = queryCache.get<T>(key);
	if (cached !== null) {
		return cached;
	}

	// Execute query and cache result
	const result = await queryFn();
	queryCache.set(key, result, ttlMs);

	return result;
}

/**
 * Cache invalidation helpers
 */
export const cacheInvalidators = {
	/**
	 * Invalidate user-specific caches when user creates/updates reviews
	 */
	onUserReviewChange(userId: string): void {
		queryCache.invalidatePattern(`user_stats:${userId}`);
		queryCache.invalidatePattern(`user_artists:${userId}`);
		queryCache.invalidatePattern(`user_festivals:${userId}`);
		queryCache.invalidatePattern(`facepile:`); // Invalidate all facepile data
	},

	/**
	 * Invalidate social caches when follow relationships change
	 */
	onFollowChange(userId: string): void {
		queryCache.invalidatePattern(`social_counts:${userId}`);
		queryCache.invalidatePattern(`facepile:`); // Facepile depends on follow relationships
	},

	/**
	 * Invalidate festival-specific caches
	 */
	onFestivalChange(festivalId: string): void {
		queryCache.invalidatePattern(`festival_stats:${festivalId}`);
		queryCache.invalidatePattern(`facepile:festival:${festivalId}`);
	},

	/**
	 * Invalidate show-specific caches
	 */
	onShowChange(showId: string): void {
		queryCache.invalidatePattern(`show_stats:${showId}`);
		queryCache.invalidatePattern(`facepile:show:${showId}`);
	},
};
